/**
 * Scoped localStorage — keys are namespaced per user.
 *
 * - Logged-in users: `tile-studio:<pubkey12>:<key>`
 * - Guest (logged out): `tile-studio:guest:<key>`
 *
 * When a user logs in for the first time, they get fresh defaults —
 * guest config is NOT inherited.
 */

export type StorageScope = { type: 'guest' } | { type: 'user'; pubkey: string };

// ---------------------------------------------------------------------------
// Module-level active scope (set by StorageScopeProvider on auth change)
// ---------------------------------------------------------------------------

let _activeScope: StorageScope = { type: 'guest' };

/** Called by the scope provider whenever the logged-in user changes. */
export function setActiveScope(scope: StorageScope): void {
  _activeScope = scope;
}

export function getActiveScope(): StorageScope {
  return _activeScope;
}

// ---------------------------------------------------------------------------
// Scoped key helpers
// ---------------------------------------------------------------------------

function prefix(scope: StorageScope): string {
  if (scope.type === 'guest') return 'tile-studio:guest:';
  return `tile-studio:${scope.pubkey.slice(0, 12)}:`;
}

function scopedKey(scope: StorageScope, key: string): string {
  return `${prefix(scope)}${key}`;
}

// ---------------------------------------------------------------------------
// Public API — uses active scope by default
// ---------------------------------------------------------------------------

export function scopedGet(key: string, scope?: StorageScope): string | null {
  return localStorage.getItem(scopedKey(scope ?? _activeScope, key));
}

export function scopedSet(key: string, value: string, scope?: StorageScope): void {
  localStorage.setItem(scopedKey(scope ?? _activeScope, key), value);
}
